import { useTranslation } from 'react-i18next';
import { cn } from '@hero/web-lib';
import { LenderChip } from '../LenderChip/LenderChip';
import type { LenderDisplay } from '../types';

export type FinancingPartnersStripProps = {
  /**
   * The active pilot lenders. Caller supplies the list — the
   * carrier dashboard passes the mock; an admin / marketing page
   * can supply real `LenderContact` rows from the API.
   */
  lenders: LenderDisplay[];
  /** Optional title override; defaults to `landing.partners_strip.title`. */
  title?: string;
  /** Lender chip size; defaults to `md`. */
  size?: 'sm' | 'md' | 'lg';
  className?: string;
};

/**
 * "In partnership with" strip. Visually anchors a list of pilot
 * lenders to a page section as social proof. Renders nothing if
 * `lenders` is empty so the strip self-hides on partner-less
 * pages without a guard at the call site.
 */
export function FinancingPartnersStrip({
  lenders,
  title,
  size = 'md',
  className,
}: FinancingPartnersStripProps) {
  const { t } = useTranslation('m-financing');
  if (!lenders.length) return null;

  return (
    <section
      className={cn(
        'flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-gray-200 bg-white/60 px-5 py-4 backdrop-blur',
        className,
      )}>
      <span className="text-sm font-semibold uppercase tracking-wide text-gray-600">
        {title ?? t('landing.partners_strip.title')}
      </span>
      <div className="flex flex-wrap items-center gap-3">
        {lenders.map(lender => (
          <LenderChip key={lender.id} lender={lender} size={size} />
        ))}
      </div>
    </section>
  );
}
